import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { ToastContainer } from "react-toastify";
import PropTypes from "prop-types";
import { yupResolver } from "@hookform/resolvers/yup";
import { getAllAddOns } from "../addOns/AddOnsService";
import KeycloakService from "../config/KeycloakService";
import {
  checkReservationAvailability,
  createReservation,
  estimateReservationPrice,
} from "./ReservationServices";
import FacilityConstants from "../../utilities/constants/FacilityConstants";
import {
  USDollar,
  timeOptions,
  formatDateForInput,
  generateInitialSubtotalBreakDown,
  computeDurationInHours,
  ceilToHalfHour,
} from "../../utilities/functions/Helpers";
import { EntitiesListResponseModel } from "../../utilities/models/EntitiesListResponseModel";
import {
  ReservationModel,
  reservationSchema,
} from "../../utilities/models/ReservationModel";
import AddonsList from "../addOns/AddOnsList";
import { Services } from "../../utilities/models/ServiceModel";
import { useLocation } from "react-router-dom";
import { useUserRefresh, useUser } from "../user/UserProvider";
import { useLoadingSpinner } from "../../utilities/components/LoadingSpinnerProvider";

const getSubmitConflictMessage = (error) => {
  const data = error?.response?.data;

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors[0];
  }

  return data?.message || "The chosen interval is already booked.";
};

/**
 * The reservation form
 * @param {object} props - The props passed to the component when updating a reservation.
 * @returns
 */
const ReservationForm = (props) => {
  const {
    reservation,
    updateReservation,
    toggleModal,
    setState,
    isUpdating = false,
    onSuccess,
  } = props;

  const setIsLoading = useLoadingSpinner();
  const locationState = useLocation().state;
  const setFetchUserFlag = useUserRefresh();
  const { userProfile } = useUser();
  const availabilityRequestIdRef = useRef(0);

  const [conflictingSchedule, setConflictingSchedule] = useState(false);
  const [availabilityMessage, setAvailabilityMessage] = useState("");
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [pricingError, setPricingError] = useState(false);
  const {
    clearErrors,
    control,
    getValues,
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(reservationSchema),
    defaultValues: {
      id: reservation?.id ?? null,
      startingDateTime: reservation?.startingDateTime
        ? formatDateForInput(reservation?.startingDateTime)
        : "",
      endingDateTime: reservation?.endingDateTime
        ? formatDateForInput(reservation?.endingDateTime)
        : "",
      effectiveEndingDateTime: reservation?.effectiveEndingDateTime
        ? formatDateForInput(reservation?.effectiveEndingDateTime)
        : "",
      eventType: reservation?.eventType ?? locationState?.eventType ?? "",
      numberOfSeats: reservation?.numberOfSeats ?? "",
      addOns: reservation?.addOns ?? [],
      addOnsTotalCost: reservation?.addOnsTotalCost ?? 0,
      status: reservation?.status ?? FacilityConstants.STATUS.PENDING,
      fullPackage: reservation?.fullPackage ?? false,
      securityDepositRefunded: reservation?.securityDepositRefunded ?? false,
      taxRate: reservation?.taxRate ?? FacilityConstants.TAX_RATE,
      totalPrice: reservation?.totalPrice ?? 0,
      priceComputationMethod:
        reservation?.priceComputationMethod ??
        FacilityConstants.COMPUTATION_METHOD.AUTO,
      pricingStrategy:
        reservation?.pricingStrategy ??
        ReservationModel.pricingStrategy,
      reminderPreferences: reservation?.reminderPreferences ?? "EMAIL",
      userId: reservation?.userId ?? KeycloakService.getUserId(),
    },
  });

  const now = new Date().toLocaleString("en-US", timeOptions);
  const todayAt12AM = new Date(now).setHours(0, 0, 0, 0);
  const nextDayAt12AM = new Date(todayAt12AM).setHours(20, 0, 0, 0);

  const minDateTime =
    new Date(now) > new Date(now).setHours(19, 30, 0, 0)
      ? new Date(nextDayAt12AM).toISOString().slice(0, 16)
      : new Date(todayAt12AM).toISOString().slice(0, 16);

  const initialSubBreakDown = generateInitialSubtotalBreakDown(reservation);

  const [addOnsData, setAddOnsData] = useState(EntitiesListResponseModel);
  const [subTotal, setSubTotal] = useState(
    reservation ? reservation.totalPrice / (1 + reservation.taxRate) : 0,
  );
  const [tax, setTax] = useState(
    reservation ? reservation.totalPrice - subTotal : 0,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilters, setCategoryFilters] = useState([]);
  const [subTotalBreakDown, setSubTotalBreakDown] =
    useState(initialSubBreakDown);

  const watchedStartingDateTime = watch("startingDateTime");
  const watchedEndingDateTime = watch("endingDateTime");
  const watchedEffectiveEndingDateTime = watch("effectiveEndingDateTime");
  const watchedPricingStrategy = watch("pricingStrategy");
  const watchedTaxRate = watch("taxRate");
  const watchedTotalPrice = watch("totalPrice");

  const pricingPreview = useMemo(() => {
    if (!watchedStartingDateTime || !watchedEndingDateTime) {
      return null;
    }

    const durationHours = computeDurationInHours(
      watchedStartingDateTime,
      watchedEndingDateTime,
    );

    if (!Number.isFinite(durationHours) || durationHours <= 0) {
      return null;
    }

    const bookedHours = ceilToHalfHour(durationHours);
    const hourlyRate = FacilityConstants.HOURLY_FACILITY_RATE;
    const hourlyEstimate = bookedHours * hourlyRate;
    const startDay = new Date(watchedStartingDateTime);
    const isSaturday =
      FacilityConstants.WEEK_DAYS[startDay.getDay()] ===
      FacilityConstants.WEEK_DAYS.at(-1);
    const fullDayEstimate = isSaturday
      ? FacilityConstants.SATURDAY_RATE
      : FacilityConstants.REGULAR_DAYS_RATE;

    return {
      bookedHours,
      hourlyRate,
      hourlyEstimate,
      fullDayEstimate,
      savings: Math.abs(fullDayEstimate - hourlyEstimate),
      hourlyBetter: hourlyEstimate < fullDayEstimate,
      hourlyDisabled: durationHours > FacilityConstants.HOURLY_MAX_HOURS,
      isSaturday,
    };
  }, [watchedEndingDateTime, watchedStartingDateTime]);

  const isHourlyPricing =
    watchedPricingStrategy === FacilityConstants.PRICING_STRATEGY.HOURLY;
  const hasActualOvertime = Number(subTotalBreakDown.overtime.totalCost ?? 0) > 0;

  const applyEstimateToForm = (estimate) => {
    if (!estimate) {
      return;
    }

    const breakdown = estimate.breakdown ?? {};
    const nextSubTotal = Number(estimate.subtotal ?? 0);
    const nextTax = Number(estimate.tax ?? 0);
    const nextTotal = Number(estimate.total ?? 0);
    const nextTaxRate = Number(estimate.taxRate ?? FacilityConstants.TAX_RATE);

    setSubTotal(nextSubTotal);
    setTax(nextTax);
    setPricingError(false);
    setValue("taxRate", nextTaxRate, { shouldDirty: true });
    setValue("totalPrice", nextTotal, { shouldDirty: true });
    setValue(
      "pricingStrategy",
      estimate.pricingStrategy ?? getValues().pricingStrategy ?? "FULL_DAY",
      { shouldDirty: true },
    );

    setSubTotalBreakDown({
      addOnsTotal: Number(breakdown.addOns ?? 0),
      facilityRental: Number(breakdown.facility ?? 0),
      facilityCleaningFees: Number(breakdown.cleaning ?? 0),
      overtime: {
        hours: Number(breakdown.bookedHours ?? 0),
        totalCost: Number(breakdown.overtime ?? 0),
        overtimeRate: Number(breakdown.overtimeRate ?? 0),
      },
      seats: {
        seatRateTotal: Number(breakdown.seats ?? 0),
        seatsCount: Number(getValues().numberOfSeats ?? 0),
        seatPrice: Number(breakdown.seatRate ?? 0),
      },
      bookedHours: breakdown.bookedHours ?? null,
      hourlyRate: breakdown.hourlyRate ?? null,
    });
  };

  const requestServerEstimate = async (overrides = {}) => {
    const currentValues = getValues();
    const estimateInput = {
      ...currentValues,
      ...overrides,
      addOns: overrides.addOns ?? currentValues.addOns ?? [],
      addOnsTotalCost:
        overrides.addOnsTotalCost ?? currentValues.addOnsTotalCost ?? 0,
      numberOfSeats:
        overrides.numberOfSeats ?? currentValues.numberOfSeats ?? 0,
      startingDateTime:
        overrides.startingDateTime ?? currentValues.startingDateTime,
      endingDateTime: overrides.endingDateTime ?? currentValues.endingDateTime,
      pricingStrategy:
        overrides.pricingStrategy ?? currentValues.pricingStrategy ?? "FULL_DAY",
      taxRate: overrides.taxRate ?? currentValues.taxRate ?? FacilityConstants.TAX_RATE,
    };

    if (
      !estimateInput.startingDateTime ||
      !estimateInput.endingDateTime ||
      !estimateInput.numberOfSeats
    ) {
      return;
    }

    try {
      const estimate = await estimateReservationPrice(
        {
          ...estimateInput,
          startingDateTime: new Date(estimateInput.startingDateTime).toISOString(),
          endingDateTime: new Date(estimateInput.endingDateTime).toISOString(),
        },
        setPricingError,
      );

      applyEstimateToForm(estimate);
    } catch {
      setPricingError(true);
    }
  };

  const handlePricingStrategySelect = (strategy) => {
    setValue("pricingStrategy", strategy, {
      shouldDirty: true,
      shouldValidate: true,
    });
    requestServerEstimate({ pricingStrategy: strategy });
  };

  const primaryServices = Services.slice(0, -1)
    .filter((service) => service.available)
    .sort((a, b) => a.title.localeCompare(b.title));
  const otherService = Services.at(-1);

  const singIn = async () => {
    await KeycloakService.doLogin(setFetchUserFlag);
  };

  const resetForm = () => {
    reset();
    setTax(0);
    setSubTotal(0);
    setSearchQuery("");
    setConflictingSchedule(false);
    setAvailabilityMessage("");
    setIsCheckingAvailability(false);
    setValue("addOns", []);
    setValue("addOnsTotalCost", 0);
    setValue("taxRate", FacilityConstants.TAX_RATE);
    setValue("totalPrice", 0);
    setValue("pricingStrategy", ReservationModel.pricingStrategy);
    setValue("reminderPreferences", "EMAIL");
    setCategoryFilters([]);
    setPricingError(false);
    setSubTotalBreakDown(generateInitialSubtotalBreakDown(ReservationModel));

    clearErrors();
    toggleModal !== undefined && toggleModal(false);
  };

  const onSubmit = async (data) => {
    if (conflictingSchedule) {
      return;
    }

    setConflictingSchedule(false);
    setAvailabilityMessage("");

    data.startingDateTime = new Date(data.startingDateTime).toISOString();
    data.endingDateTime = new Date(data.endingDateTime).toISOString();
    if (data.effectiveEndingDateTime) {
      data.effectiveEndingDateTime = new Date(
        data.effectiveEndingDateTime,
      ).toISOString();
    } else {
      delete data.effectiveEndingDateTime;
    }

    setIsLoading(true);
    try {
      let response;
      if (isUpdating) {
        response = await updateReservation(
          data,
          undefined,
          setState,
          setIsLoading,
        );
      } else {
        response = await createReservation(
          data,
          undefined,
          setState,
          setIsLoading,
        );
      }
      resetForm();
      onSuccess?.(response ?? data, isUpdating ? "updated" : "created");
    } catch (error) {
      if (error?.response?.status === 409) {
        setConflictingSchedule(true);
        setAvailabilityMessage(getSubmitConflictMessage(error));
      }
    }
  };

   /**
    * Handle the guest count value change.
    * @param {object} e The guest count input event.
    */
   const handleGuestCountChange = (e) => {
    let numberOfSeats = e.target.valueAsNumber;
    if (typeof numberOfSeats === "number" && !Number.isNaN(numberOfSeats)) {
      setValue("numberOfSeats", numberOfSeats, {
        shouldDirty: true,
        shouldValidate: true,
      });
      requestServerEstimate({ numberOfSeats });
    } else {
      setValue("numberOfSeats", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      setSubTotal(0);
      setTax(0);
      setPricingError(false);
      setValue("totalPrice", 0, { shouldDirty: true });
      setSubTotalBreakDown(generateInitialSubtotalBreakDown(ReservationModel));
    }
  };

  /**
   * Handle the reservation ending date and time value change.
   * @param {Object} e The reservation ending date and time value input event.
   */
  const handleEndingDatetimeChange = (onChange) => (e) => {
    const endingDateTime = e.target.value;
    onChange(endingDateTime);
    requestServerEstimate({ endingDateTime });
  };

  /**
   * Handle the reservation starting date and time value change.
   * @param {object} e The reservation starting date and time value input event.
   */
  const handleStartingDateTimeChange = (onChange) => (e) => {
    const startingDateTime = e.target.value;
    onChange(startingDateTime);
    requestServerEstimate({ startingDateTime });
  };

  /**
   * Handle the addOns' category selection.
   * @param {String} option The category selection event.
   */
  const handleCategoryFilterChange = (option) => {
    const selectedCategory = option;
    if (categoryFilters.includes(selectedCategory)) {
      setCategoryFilters(
        categoryFilters.filter((category) => category !== selectedCategory),
      );
    } else {
      setCategoryFilters([...categoryFilters, selectedCategory]);
    }
  };

  /**
   * Handle the search string change.
   * @param {object} event The search typing event.
   */
  const handleSearchQueryChange = (event) => {
    setSearchQuery(event.target.value);
  };

  /**
   * Handle the change in quantity of a selected addOn.
   * @param {object} selectedItem The addOn selected
   * @param {number} quantity The quantity desired by the user.
   */
  const handleItemQuantityChange = (selectedItem, quantity) => {
     const data = getValues();
     const prevSelectedItems = data.addOns.slice();
     const existingItemIndex = prevSelectedItems.findIndex(
       (item) => item.addOn.name === selectedItem.name,
     );
     const parsedQuantity = Number(quantity);

     if (parsedQuantity > 0) {
       if (existingItemIndex >= 0) {
         prevSelectedItems[existingItemIndex].quantity = parsedQuantity;
       }
       if (existingItemIndex === -1) {
         prevSelectedItems.push({ addOn: selectedItem, quantity: parsedQuantity });
       }
     } else if (existingItemIndex >= 0) {
       prevSelectedItems.splice(existingItemIndex, 1);
     }

     const addOnsTotalCost = prevSelectedItems.reduce(
       (acc, { addOn, quantity: selectedQuantity }) =>
         acc + addOn.price * Number(selectedQuantity),
       0,
     );

     setValue("addOnsTotalCost", addOnsTotalCost, { shouldDirty: true });
     setValue("addOns", prevSelectedItems, { shouldDirty: true });
     requestServerEstimate({
       addOns: prevSelectedItems,
       addOnsTotalCost,
     });
   };

   useEffect(() => {
     if (KeycloakService.isLoggedIn() && userProfile?.userId) {
       getAllAddOns(setAddOnsData);
     }
   }, [isUpdating, setAddOnsData, userProfile]);

   useEffect(() => {
     if (!KeycloakService.isLoggedIn()) {
       return;
     }

     const values = getValues();
      if (values.startingDateTime && values.endingDateTime && values.numberOfSeats) {
       requestServerEstimate();
     }
   }, [addOnsData, isUpdating]);

   useEffect(() => {
     if (!KeycloakService.isLoggedIn()) {
       return;
     }

     const values = getValues();
     if (!values.startingDateTime || !values.endingDateTime || !values.numberOfSeats) {
       setPricingError(false);
       return;
     }

     requestServerEstimate();
   }, []);

   useEffect(() => {
     if (!KeycloakService.isLoggedIn()) {
       return;
     }

     if (!watchedStartingDateTime || !watchedEndingDateTime) {
       setConflictingSchedule(false);
       setAvailabilityMessage("");
       setIsCheckingAvailability(false);
       return;
     }

     if (errors.startingDateTime || errors.endingDateTime) {
       setConflictingSchedule(false);
       setAvailabilityMessage("");
       setIsCheckingAvailability(false);
       return;
     }

     const requestId = ++availabilityRequestIdRef.current;
     setIsCheckingAvailability(true);

     checkReservationAvailability({
       startingDateTime: watchedStartingDateTime,
       endingDateTime: watchedEndingDateTime,
       effectiveEndingDateTime: watchedEffectiveEndingDateTime,
       reservationId: reservation?.id ?? null,
     })
       .then((result) => {
         if (requestId !== availabilityRequestIdRef.current) {
           return;
         }

         setConflictingSchedule(result.available === false);
         setAvailabilityMessage(result.available === false ? result.message ?? "" : "");
       })
       .finally(() => {
         if (requestId === availabilityRequestIdRef.current) {
           setIsCheckingAvailability(false);
         }
       });
   }, [
     watchedStartingDateTime,
     watchedEndingDateTime,
     watchedEffectiveEndingDateTime,
     reservation?.id,
     errors.startingDateTime,
     errors.endingDateTime,
   ]);

  return (
    <>
      {KeycloakService.isLoggedIn() ? (
        <div className="w-full">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
            {/* Banner Section */}
            <div className="h-32 bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 relative">
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
              <div className="absolute bottom-4 left-6 md:left-8 text-white">
                <h1 className="text-2xl md:text-3xl font-serif font-bold text-white drop-shadow-md">
                  {isUpdating ? "Edit Reservation" : "New Reservation"}
                </h1>
                <p className="text-white/80 text-sm mt-1 font-medium">
                  Create unforgettable memories with us.
                </p>
              </div>
            </div>

            <div className="px-6 py-8 md:px-8">
              <form
                className="w-full space-y-8"
                onSubmit={handleSubmit(onSubmit)}
              >
                {/* Basic Information */}
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                    <span className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </span>
                    Event Details
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Event Type */}
                    <div className="space-y-2">
                      <label
                        htmlFor="eventType"
                        className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
                      >
                        Event Type <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="eventType"
                        control={control}
                        render={({ field }) => (
                          <div className="relative">
                            <select
                              id="eventType"
                              {...field}
                              className={`w-full px-4 py-3 rounded-xl border ${errors.eventType ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none`}
                            >
                              <option value="">Select an event type</option>
                              {primaryServices.map((service) => (
                                <option key={service.id} value={service.title}>
                                  {service.title}
                                </option>
                              ))}
                              <option
                                key={otherService.id}
                                value={otherService.title}
                              >
                                {otherService.title}
                              </option>
                            </select>
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="m6 9 6 6 6-6" />
                              </svg>
                            </div>
                          </div>
                        )}
                      />
                      {errors.eventType && (
                        <p className="text-red-500 text-xs mt-1 ml-1">
                          {errors.eventType.message}
                        </p>
                      )}
                    </div>

                    {/* Guest Count */}
                    <div className="space-y-2">
                      <label
                        htmlFor="numberOfSeats"
                        className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
                      >
                        Guests Count <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="numberOfSeats"
                        control={control}
                        render={({ field }) => (
                          <input
                            id="numberOfSeats"
                            type="number"
                            max={200}
                            className={`w-full px-4 py-3 rounded-xl border ${errors.numberOfSeats ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none`}
                            {...field}
                            onChange={handleGuestCountChange}
                          />
                        )}
                      />
                      {errors.numberOfSeats && (
                        <p className="text-red-500 text-xs mt-1 ml-1">
                          {errors.numberOfSeats.message}
                        </p>
                      )}
                    </div>

                    {/* Start Date */}
                    <div className="space-y-2">
                      <label
                        htmlFor="startingDateTime"
                        className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
                      >
                        Starting Date & Time{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="startingDateTime"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <input
                            type="datetime-local"
                            id="startingDateTime"
                            value={formatDateForInput(value)}
                            min={minDateTime}
                            className={`w-full px-4 py-3 rounded-xl border ${errors.startingDateTime ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none dark:calendar-invert`}
                            onChange={handleStartingDateTimeChange(onChange)}
                          />
                        )}
                      />
                      {errors.startingDateTime && (
                        <p className="text-red-500 text-xs mt-1 ml-1">
                          {errors.startingDateTime.message}
                        </p>
                      )}
                    </div>

                    {/* End Date */}
                    <div className="space-y-2">
                      <label
                        htmlFor="endingDateTime"
                        className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
                      >
                        Ending Date & Time{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="endingDateTime"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <input
                            id="endingDateTime"
                            type="datetime-local"
                            value={formatDateForInput(value)}
                            className={`w-full px-4 py-3 rounded-xl border ${errors.endingDateTime ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none dark:calendar-invert`}
                            onChange={handleEndingDatetimeChange(onChange)}
                          />
                        )}
                      />
                      {errors.endingDateTime && (
                        <p className="text-red-500 text-xs mt-1 ml-1">
                          {errors.endingDateTime.message}
                        </p>
                      )}
                    </div>

                    {(isCheckingAvailability || conflictingSchedule) && (
                      <div className="md:col-span-2">
                        {isCheckingAvailability && !conflictingSchedule && (
                          <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-700 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200">
                            Checking whether this time frame is still available...
                          </div>
                        )}

                        {conflictingSchedule && availabilityMessage && (
                          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
                            {availabilityMessage}
                          </div>
                        )}
                      </div>
                    )}

                    {pricingPreview && (
                      <div className="space-y-3 md:col-span-2">
                        <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1 block">
                          Pricing Plan
                        </label>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <button
                            type="button"
                            disabled={pricingPreview.hourlyDisabled}
                            onClick={() => handlePricingStrategySelect("HOURLY")}
                            className={`relative rounded-2xl border-2 p-5 text-left transition-all ${
                              pricingPreview.hourlyDisabled
                                ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-60 dark:border-slate-700 dark:bg-slate-800"
                                : isHourlyPricing
                                  ? "border-blue-500 bg-blue-50 shadow-md dark:bg-blue-900/30"
                                  : "border-slate-200 bg-white hover:border-blue-300 dark:border-slate-700 dark:bg-slate-800"
                            }`}
                          >
                            {!pricingPreview.hourlyDisabled && pricingPreview.hourlyBetter && (
                              <span className="absolute -top-2 right-3 rounded-full bg-green-500 px-2 py-0.5 text-xs font-bold text-white">
                                Best Value
                              </span>
                            )}
                            <p className="text-sm font-semibold text-slate-900 dark:text-white">
                              Hourly Rate
                            </p>
                            <p className="mt-2 text-2xl font-bold text-blue-600 dark:text-blue-400">
                              {USDollar.format(pricingPreview.hourlyRate)}/h
                            </p>
                            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                              {pricingPreview.bookedHours}h × {USDollar.format(pricingPreview.hourlyRate)} = {" "}
                              {USDollar.format(pricingPreview.hourlyEstimate)}
                            </p>
                            {pricingPreview.hourlyDisabled && (
                              <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-300">
                                Hourly pricing is available for reservations up to {FacilityConstants.HOURLY_MAX_HOURS} hours.
                              </p>
                            )}
                          </button>

                          <button
                            type="button"
                            onClick={() => handlePricingStrategySelect("FULL_DAY")}
                            className={`relative rounded-2xl border-2 p-5 text-left transition-all ${
                              !isHourlyPricing
                                ? "border-blue-500 bg-blue-50 shadow-md dark:bg-blue-900/30"
                                : "border-slate-200 bg-white hover:border-blue-300 dark:border-slate-700 dark:bg-slate-800"
                            }`}
                          >
                            {!pricingPreview.hourlyDisabled && !pricingPreview.hourlyBetter && (
                              <span className="absolute -top-2 right-3 rounded-full bg-green-500 px-2 py-0.5 text-xs font-bold text-white">
                                Best Value
                              </span>
                            )}
                            {pricingPreview.hourlyDisabled && (
                              <span className="absolute -top-2 right-3 rounded-full bg-blue-500 px-2 py-0.5 text-xs font-bold text-white">
                                Recommended
                              </span>
                            )}
                            <p className="text-sm font-semibold text-slate-900 dark:text-white">
                              Full-Day Rate
                            </p>
                            <p className="mt-2 text-2xl font-bold text-blue-600 dark:text-blue-400">
                              {USDollar.format(pricingPreview.fullDayEstimate)}
                            </p>
                            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                              {pricingPreview.isSaturday ? "Saturday" : "Sun–Fri"} flat rate
                            </p>
                          </button>
                        </div>

                        {!pricingPreview.hourlyDisabled && pricingPreview.savings > 0 && (
                          <p className="text-sm font-medium text-green-600 dark:text-green-400 text-center">
                            💡 {pricingPreview.hourlyBetter ? "Hourly" : "Full-Day"} saves you {USDollar.format(pricingPreview.savings)} on facility fees.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Reminder Preferences */}
                    <div className="space-y-2">
                      <label
                        htmlFor="reminderPreferences"
                        className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
                      >
                        Reminder Preferences
                      </label>
                      <div className="relative">
                        <select
                          id="reminderPreferences"
                          className={`w-full px-4 py-3 rounded-xl border ${errors.reminderPreferences ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none`}
                          {...register("reminderPreferences")}
                        >
                          <option value="EMAIL">Email</option>
                          <option value="SMS">SMS</option>
                          <option value="EMAIL,SMS">Email & SMS</option>
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="m6 9 6 6 6-6" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Add-Ons */}
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                    <span className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg text-purple-600 dark:text-purple-400">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 2v20" />
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                      </svg>
                    </span>
                    Configure Add-Ons
                  </h3>
                  <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
                    <AddonsList
                      categoryFilters={categoryFilters}
                      searchQuery={searchQuery}
                      onCategoryFilterChange={handleCategoryFilterChange}
                      onSearchQueryChange={handleSearchQueryChange}
                      onItemQuantityChange={handleItemQuantityChange}
                      requestedAddOns={getValues().addOns ?? []}
                      addOnsData={addOnsData.content}
                    />
                  </div>
                </div>

                {/* Price Breakdown */}
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                    <span className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg text-green-600 dark:text-green-400">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect width="20" height="14" x="2" y="5" rx="2" />
                        <line x1="2" x2="22" y1="10" y2="10" />
                      </svg>
                    </span>
                    Estimated Cost
                  </h3>
                  <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-6 border border-slate-200 dark:border-slate-800 space-y-3 text-slate-700 dark:text-slate-300">
                    {pricingError && (
                      <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
                        We couldn't refresh the live estimate right now. Please review your details and try again.
                      </div>
                    )}
                    <div className="flex justify-between items-center text-sm">
                      <span>Add-Ons Total</span>
                      <span className="font-medium">
                        {USDollar.format(subTotalBreakDown.addOnsTotal ?? 0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span>Facility Rental</span>
                      <span className="font-medium">
                        {USDollar.format(subTotalBreakDown.facilityRental)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span>Cleaning Fees</span>
                      <span className="font-medium">
                        {USDollar.format(
                          subTotalBreakDown.facilityCleaningFees,
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span>
                        {`Seat Charges (${subTotalBreakDown.seats.seatsCount} x `}
                        {USDollar.format(subTotalBreakDown.seats.seatPrice)})
                      </span>
                      <span className="font-medium">
                        {USDollar.format(subTotalBreakDown.seats.seatRateTotal)}
                      </span>
                    </div>
                    {hasActualOvertime && (
                      <div className="flex justify-between items-center text-sm">
                        <span>
                          Overtime ({subTotalBreakDown.overtime.hours.toFixed(2)}h)
                        </span>
                        <span className="font-medium">
                          {USDollar.format(subTotalBreakDown.overtime.totalCost)}
                        </span>
                      </div>
                    )}

                    {subTotalBreakDown.bookedHours != null && (
                      <div className="flex justify-between items-center text-sm">
                        <span>{isHourlyPricing ? "Booked Time" : "Booked Hours"}</span>
                        <span className="font-medium">
                          {Number(subTotalBreakDown.bookedHours).toFixed(2)}h
                        </span>
                      </div>
                    )}

                    {subTotalBreakDown.hourlyRate != null && (
                      <div className="flex justify-between items-center text-sm">
                        <span>Hourly Rate</span>
                        <span className="font-medium">
                          {USDollar.format(subTotalBreakDown.hourlyRate)}
                        </span>
                      </div>
                    )}

                    <div className="border-t border-slate-200 dark:border-slate-700 my-4 pt-4 space-y-2">
                      <div className="flex justify-between items-center text-base">
                        <span className="font-semibold">Subtotal</span>
                        <span className="font-bold">
                          {USDollar.format(subTotal)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-base">
                        <span className="font-semibold">
                          Tax ({((watchedTaxRate ?? FacilityConstants.TAX_RATE) * 100).toFixed(2)}%)
                        </span>
                        <span className="font-bold">
                          {USDollar.format(tax)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-xl text-blue-600 dark:text-blue-400 mt-2">
                        <span className="font-bold">Total</span>
                        <span className="font-extrabold">
                          {USDollar.format(watchedTotalPrice ?? 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-row justify-end gap-4 pt-6 mt-8 border-t border-slate-200 dark:border-slate-800">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-6 py-3 rounded-xl font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isCheckingAvailability || conflictingSchedule}
                    className={`px-8 py-3 rounded-xl font-bold text-white shadow-lg transition-all ${
                      isCheckingAvailability || conflictingSchedule
                        ? "cursor-not-allowed bg-slate-400 shadow-none dark:bg-slate-600"
                        : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 shadow-blue-500/30 hover:-translate-y-0.5"
                    }`}
                  >
                    {isCheckingAvailability
                      ? "Checking availability..."
                      : isUpdating
                        ? "Update Reservation"
                        : "Submit Request"}
                  </button>
                </div>
              </form>
             </div>
           </div>
           <ToastContainer position="bottom-right" theme="colored" />
         </div>
       ) : (
         <div className="text-center p-12">
           <div className="inline-flex items-center justify-center p-6 bg-slate-100 dark:bg-slate-800 rounded-full mb-6 relative overflow-hidden group">
             <div className="absolute inset-0 bg-blue-500/10 scale-0 group-hover:scale-100 transition-transform rounded-full"></div>
             <span className="text-4xl relative z-10">🔒</span>
           </div>
           <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
             Login Required
           </h2>
           <p className="text-slate-500 dark:text-slate-400 mb-8 max-w-md mx-auto">
             Please sign in to view and manage your reservations securely.
           </p>
           <button
             onClick={() => singIn()}
             className="px-8 py-3 bg-blue-600 text-white font-bold rounded-xl shadow-lg hover:bg-blue-700 transition-all hover:-translate-y-0.5"
           >
             Sign In to Continue
           </button>
         </div>
       )}
     </>
   );
};

ReservationForm.propTypes = {
  reservation: PropTypes.object,
  updateReservation: PropTypes.func,
  toggleModal: PropTypes.func,
  setState: PropTypes.func,
  isUpdating: PropTypes.bool,
  onSuccess: PropTypes.func,
};

export default ReservationForm;
